import axios from 'axios';
import { toast } from 'react-toastify';

const BASE = (process.env.REACT_APP_EXPENSE_API_URL || 'https://saviagent.savictech.com/expense-policy/api/v1').replace(/\/$/, '');

// Azure AD SSO — auth IS under /api/v1; backend redirects back with ?token=<jwt>
export const getAzureLoginUrl = () => {
  const redirectBack = `${window.location.origin}/expense`;
  return `${BASE}/auth/azure/login?frontend_redirect=${encodeURIComponent(redirectBack)}`;
};

const TOKEN_KEY   = 'epe_token';
const PROFILE_KEY = 'epe_user_profile';

export const getToken       = ()  => localStorage.getItem(TOKEN_KEY);
export const setToken       = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken     = ()  => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PROFILE_KEY);
};
export const setUserProfile = (p) => localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
export const getUserProfile = ()  => {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY)); } catch { return null; }
};

export const parseJwt = (token) => {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
};

export const getCurrentUser = () => {
  const profile = getUserProfile();
  if (profile) return profile;
  const t = getToken();
  return t ? parseJwt(t) : null;
};

// JWT payload: { sub, oid, preferred_username, name, roles, iat, exp, iss }
export const getUserRoles       = () => getCurrentUser()?.roles || [];
export const getUserEmail       = () => getCurrentUser()?.preferred_username || getCurrentUser()?.sub || '';
export const getUserDisplayName = () => getCurrentUser()?.name || getUserEmail();

export const isExpenseApprover = () => ['ExpenseApprover', 'ExpenseAdmin'].some(r => getUserRoles().includes(r));
export const isExpenseAdmin    = () => getUserRoles().includes('ExpenseAdmin');
export const isFinanceManager  = () => isExpenseAdmin();

export const isTokenExpired = () => {
  const t = getToken();
  if (!t) return true;
  const payload = parseJwt(t);
  if (!payload?.exp) return false;
  return Date.now() / 1000 > payload.exp;
};

// ── Axios instance ─────────────────────────────────────────────────────────
const api = axios.create({ baseURL: BASE });

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    const status = err.response?.status;
    if (status === 401 && getToken()) {
      clearToken();
      window.dispatchEvent(new Event('epe:logout'));
      toast.error('Session expired. Please sign in again.');
    }
    return Promise.reject(err);
  }
);

// Error shape: { error_code, message, details: [{ field, message }], request_id, timestamp }
export const errMsg = (err, fallback = 'An error occurred.') => {
  const data = err.response?.data;
  if (!data) return fallback;
  if (typeof data.message === 'string') {
    if (Array.isArray(data.details) && data.details.length > 0) {
      const fields = data.details.map(d => `${d.field}: ${d.message}`).join(' · ');
      return `${data.message} (${fields})`;
    }
    return data.message;
  }
  return fallback;
};

// ── Claims ──────────────────────────────────────────────────────────────────
export const submitClaim = (payload) =>
  api.post('/claims', payload).then(r => r.data);

export const bulkSubmitClaims = (claims) =>
  api.post('/claims/bulk', { claims }).then(r => r.data);

export const listClaims = (params = {}) =>
  api.get('/claims', { params }).then(r => r.data);

export const getClaim = (claimId) =>
  api.get(`/claims/${claimId}`).then(r => r.data);

export const updateClaim = (claimId, payload) =>
  api.put(`/claims/${claimId}`, payload).then(r => r.data);

export const deleteClaim = (claimId) =>
  api.delete(`/claims/${claimId}`).then(r => r.data);

export const approveClaim = (claimId) =>
  api.post(`/claims/${claimId}/approve`).then(r => r.data);

// ── Approvals ───────────────────────────────────────────────────────────────
export const listPendingApprovals = (tier) =>
  api.get('/approvals/pending', { params: tier != null ? { tier } : {} }).then(r => r.data);

export const actionApproval = (approvalId, action, comments = '') =>
  api.post(`/approvals/${approvalId}/action`, { action, comments }).then(r => r.data);

// ── Receipts ─────────────────────────────────────────────────────────────────
export const uploadReceipt = (file, claimId, lineId) => {
  const form = new FormData();
  form.append('file', file);
  form.append('claim_id', claimId);
  if (lineId != null) form.append('line_id', String(lineId));
  // Do NOT set Content-Type — browser sets it automatically with multipart boundary
  return api.post('/receipts', form).then(r => r.data);
};

export const getReceipt = (receiptId) =>
  api.get(`/receipts/${receiptId}`).then(r => r.data);

// ── Policy ───────────────────────────────────────────────────────────────────
export const listPolicyRules = (params = {}) =>
  api.get('/policy/rules', { params }).then(r => r.data);

export const upsertPolicyRule = (rule) =>
  api.put('/policy/rules', rule).then(r => r.data);

export const listPerDiem = (params = {}) =>
  api.get('/policy/perdiem', { params }).then(r => r.data);

export const listBlacklist = (params = {}) =>
  api.get('/policy/merchants/blacklist', { params }).then(r => r.data);

export const addToBlacklist = (payload) =>
  api.post('/policy/merchants/blacklist', payload).then(r => r.data);

// ── NLP Query ────────────────────────────────────────────────────────────────
export const nlpQuery = (question) =>
  api.post('/query', { question }).then(r => r.data);

// ── Reports ───────────────────────────────────────────────────────────────────
export const getViolationsReport = (params = {}) =>
  api.get('/reports/violations', { params }).then(r => r.data);

export const getSlaReport = () =>
  api.get('/reports/sla').then(r => r.data);

export const getSpendReport = (params = {}) =>
  api.get('/reports/spend', { params }).then(r => r.data);

export const getAuditLog = (params = {}) =>
  api.get('/reports/audit-log', { params }).then(r => r.data);

// ── Health ────────────────────────────────────────────────────────────────────
export const healthCheck = () =>
  axios.get(`${BASE}/health`).then(r => r.data);

export { BASE as EXPENSE_API_BASE_URL };
